import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { Components, Files, WebserviceResponse } from '../interfaces';
import { catchError, map, retry } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService, IAlertType } from "./alert.service";
import { filesBaseUrl, restBaseUrl } from "../utils/rest-url";
import { normalizeSingletonResponse } from "../utils/response";
import { getTenantStorage } from '../utils/tenant-context';

@Injectable({
  providedIn: 'root'
})
export class ComponentsStoreService {

  private readonly endpoint = restBaseUrl('/components');
  private readonly filesdirectory = filesBaseUrl();

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService
  ) { }

  private authHeaders() {
    const token = getTenantStorage('access_token');
    if (!token || token === 'null' || token === 'undefined') {
      return {};
    }
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`
      })
    };
  }

  private handleError(error: HttpErrorResponse){
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  public getComponents(active: boolean) : Observable<WebserviceResponse<Components[]>> {
    const options = new HttpParams({
      fromObject: {
        technical_drawing: '0',
        active: active
      }
    });
    // console.log('Get more components!');
    return this.http.get<WebserviceResponse<Components[]>>(this.endpoint + '/get_components',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getAssemblies() : Observable<WebserviceResponse<Components[]>> {
    const options = new HttpParams({
      fromObject: {}
    });
    // console.log('Get more components!');
    return this.http.get<WebserviceResponse<Components[]>>(this.endpoint + '/get_assemblies',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getTechnologies() : Observable<WebserviceResponse<Components[]>> {
    const options = new HttpParams({
      fromObject: {
        searchterm: '',
        technology: 'true',
        standard: 'false'
      }
    });
    return this.http.get<WebserviceResponse<Components[]>>(this.endpoint + '/search',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getStandardParts() : Observable<WebserviceResponse<Components[]>> {
    const options = new HttpParams({
      fromObject: {
        searchterm: '',
        technology: 'false',
        standard: 'true'
      }
    });
    return this.http.get<WebserviceResponse<Components[]>>(this.endpoint + '/search',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getSingleComponent(drawid: string) : Observable<WebserviceResponse<Components[]>> {
    const options = new HttpParams({
      fromObject: {
        technical_drawing: drawid
      }
    });
    // console.log('Get single component!');
    return this.http.get<WebserviceResponse<Components[]>>(this.endpoint + '/get_single_component',
        { params: options}).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getComponentsChildren(drawid: string, technology: string) : Observable<WebserviceResponse<Components[]>> {
    const options = new HttpParams({
      fromObject: {
        technical_drawing: drawid,
        technology: technology
      }
    });
    return this.http.get<WebserviceResponse<Components[]>>(this.endpoint + '/get_component_children',
        { params: options}).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getAllSearch(searchTerm: string, technology: string, standard: string, active: Boolean): Observable<WebserviceResponse<Components[]>> {
    return this.http.get<WebserviceResponse<Components[]>>(
        `${this.endpoint}/search?searchterm=${searchTerm}&standard=${standard}&technology=${technology}&active=${active}`
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public searchDB(searchTerm: string, technology: string): Observable<WebserviceResponse<Components[]>> {
    return this.http.get<WebserviceResponse<Components[]>>(
        `${this.endpoint}/search_adding?searchterm=${searchTerm}&technology=${technology}`
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getComponentsFile(docType: number[], projectID: string, componentID: string, subID: number, jobID: number): Observable<WebserviceResponse<Files[]>>{
    const doctypeParam = docType.join(',');
    const options = new HttpParams({
      fromObject: {
        company_id: 0,
        component: componentID,
        doctype: doctypeParam,
        job_number: jobID,
        project: projectID,
        sub_number: subID,
      }
    });
    // console.log('Get projects/components files!');
    return this.http.get<WebserviceResponse<Files[]>>(this.filesdirectory + '/get_files',
        { params: options}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateComponent(component: Components) {
    const taricValue = (component as { taric?: string | null }).taric?.trim();
    const normalizedTaric =
      taricValue === '0' || taricValue === '' || taricValue == null
        ? null
        : taricValue;
    // Ensure all required fields are present and in correct format
    const payload = {
      technical_drawing: component.technical_drawing,
      name: component.name,
      description: component.description ?? '',
      specification: component.specification ?? '',
      material: component.material ?? '',
      material_id: component.material_id ?? null,
      manufacturer: component.manufacturer ?? '',
      guideline_price: component.guideline_price ?? null,
      // Gewicht kann 0 sein – nicht in leeren String coerzen
      weight: (component as any).weight ?? 0,
      // TARIC bleibt als String, damit führende Nullen erhalten bleiben.
      taric: normalizedTaric,
      // Lagerbestand: 0 ist gültig
      number_of_pieces_in_stock: component.number_of_pieces_in_stock ?? 0,
      technology: component.technology ?? false,
      standard: component.standard ?? false,
      active: component.active ?? true
    };

    return this.http.put<WebserviceResponse<Components>>(
      `${this.endpoint}/update_components`,
      payload,
      this.authHeaders()
    ).pipe(
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  public updateSpecification(assembly: Components) {
    return this.http.put(
        `${this.endpoint}/update_specification`,
        assembly,
        { responseType: 'json', ...this.authHeaders() }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public addComponent(component: Components) {
    const staffId = parseInt(getTenantStorage('staff_id') ?? getTenantStorage('user_id') ?? '0', 10);
    const taricValue = (component as { taric?: string | null }).taric?.trim();
    const normalizedTaric =
      taricValue === '0' || taricValue === '' || taricValue == null
        ? null
        : taricValue;
    const payload = {
      stepI: {
        technical_drawing: component.technical_drawing
      },
      stepII: {
        name: component.name,
        description: component.description || '',
        specification: component.specification || '',
        material: component.material || '',
        material_id: component.material_id ?? null,
        manufacturer: component.manufacturer ?? '',
        guideline_price: component.guideline_price ?? null,
        weight: component.weight || 0,
        taric: normalizedTaric,
        number_of_pieces_in_stock: component.number_of_pieces_in_stock ?? 0,
        staff_id: staffId,
        technology: component.technology || false,
        standard: component.standard || false,
        active: component.active !== undefined ? component.active : true
      },
      stepIII: {}
    };

    return this.http.post<WebserviceResponse<Components>>(
      `${this.endpoint}/add_component`,
      payload,
      this.authHeaders()
    ).pipe(
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  public addComponentsChild(componentChild: string, componentParent:string, partsInComponent:number) {
    return this.http.post(
        `${this.endpoint}/add_child_to_parent?child=${componentChild}&parent=${componentParent}&number_of_subparts=${partsInComponent}`,
        { responseType: 'json', ...this.authHeaders() }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteComponent(component: string, parent: number){
    return this.http.delete(
        `${this.endpoint}/delete_components?technical_drawing=${component}&delete=${parent}`,
        { responseType: 'json', ...this.authHeaders() }
    ).pipe(

        catchError((error: HttpErrorResponse) => {
          console.error('Fehler beim Löschen der Komponente:' + error.status, error); // Debug


          // Überprüfung, ob der Server eine Fehlerantwort gesendet hat
          if (error.status === 555) {
            // Die Fehlerstruktur direkt weitergeben
            // return throwError(() => error.error.description);
            return throwError(() => ({
              errorCode: error.status || 'xxx unknown',
              description: error.statusText || 'xxx Unbekannter Fehler'
            }));
          }

          // Generische Fehlerbehandlung für unerwartete Fehler
          return throwError(() => ({
            errorCode: error.status || 'unknown',
            description: error.statusText || 'Unbekannter Fehler'
          }));
        })
    );
  }

  public removeComponentsChild(componentParent: string, componentChild: string, childsPosition: number){
    return this.http.delete(
        `${this.endpoint}/delete_sub_component_from_parent?parent_component=${componentParent}&position=${childsPosition}&sub_component=${componentChild}`,
        { responseType: 'json', ...this.authHeaders() }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  getComponent(technical_drawing: string) {
    return this.http.get<WebserviceResponse<Components>>(`${this.endpoint}/get_single_component`, {
      params: new HttpParams().set('technical_drawing', technical_drawing)
    }).pipe(
      map((response) => normalizeSingletonResponse(response)),
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }
}
